import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles, Box, Typography, LinearProgress } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useDispatch, useSelector } from 'react-redux';
import MessageBubble from './MessageBubble';
import AIAssistanceEmptyState from './AIAssistanceEmptyState';
import { handleSnackBar, invokeFileDownloadService, invokeGetService, invokePostService, setAiAutoProceed, setAiRequestEntity, setInnerScreenRender, setLlmQuery, setReRender, updateAiAssistant } from '../../../store/global/actions';
import { buildChatHistory, convertToTestsuiteString, hasArrayDifference } from '../../../utils/llmUtils';
import { getAiAssistantAutoProceed, getAiAssistantContext } from '../../../store/global/selectors';
import { generateMongoId } from '../../../utils/commonUtils';
import { getDetailsFromUrl } from '../../../services/blueprint';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import './AssistanceStyles.css'
import ChatInput from './AIAssistanceChatInput';
import SystemModelUpdateAlertContent from '../PromptModal/SystemModelUpdateAlertContent';
import AIGeneratedList from './AIGeneratedList';
import WorkflowEmptyState from './WorkflowEmptyState';
import { generateViewTransactionWithAI } from '../../../services/llm/views';
import { getFunctionalTransactions } from '../../../utils/transactionUtils';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ClarificationQuestion from './AIClarificationQuestions';
import GenerationSummary from './AIGenerationSummary';

const useStyles = makeStyles((theme) => ({
  container: {
    flex: 1,
    padding: theme.spacing(4),
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: '#fff',
    position: 'relative',

    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ccc',
      borderRadius: '5px',
    },
    transition: 'height 0.5s ease',
  },
  msgWrapper: { display: 'flex', gap: theme.spacing(2), marginBottom: theme.spacing(4) },
  avatar: {
    width: 32, height: 32, borderRadius: 8, display: 'flex',
    alignItems: 'center', justifyContent: 'center', color: '#fff'
  },
  aiBubble: {
    padding: theme.spacing(3),
    borderRadius: 16,
    border: '1px solid #f1f5f9',
    backgroundColor: '#fff',
    maxWidth: '85%',
  },
  userBubble: {
    backgroundColor: '#2e2a77',
    color: '#fff',
    padding: theme.spacing(2, 3),
    borderRadius: '16px 16px 4px 16px',
    maxWidth: '75%',
    marginLeft: 'auto',
    boxShadow: '0 4px 15px rgba(46, 42, 119, 0.2)',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#94a3b8',
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  emptyIcon: {
    fontSize: 48,
    color: '#e2e8f0',
    marginBottom: theme.spacing(2),
  },
  inputBar: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    padding: '8px 12px 8px 16px',
    display: 'flex',
    alignItems: 'flex-end',
    marginTop: theme.spacing(2),
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
    transition: 'border-color 0.2s',
    '&:focus-within': {
      borderColor: '#34445c',
    }
  },
  input: {
    fontSize: 14,
    lineHeight: '1.5',
    padding: '4px 0',
  },
  sendButton: {
    backgroundColor: '#34445c',
    color: '#fff',
    borderRadius: 8,
    marginLeft: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#1e293b',
    },
    marginBottom: 2,
  },
  loaderContainer: {
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    height: 4,
    position: 'relative'
  },
  linearProgress: {
    height: 3,
    backgroundColor: 'transparent',
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: '#3B82F6',
    },
  },
  loaderGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 20,
    background: 'linear-gradient(to bottom, rgba(0, 98, 255, 0.1), transparent)',
    pointerEvents: 'none',
  },
  dotLoader: {
    content: '...',
    fontSize: 14,
    letterSpacing: 1,
    '&::after': {
      content: '...',
      animation: '$dots 1.4s steps(4, end) infinite',
    },
  },
  '@keyframes dots': {
    '0%': { content: '' },
    '33%': { content: '.' },
    '66%': { content: '..' },
    '100%': { content: '...' },
  },
  pulseDot: {
    width: 8,
    height: 8,
    backgroundColor: '#ff9800',
    borderRadius: '50%',
    boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)',
    alignItems: 'center'
  },
  animatedPulseDot: {
    animation: '$pulse 2s infinite',
  },
  alertIcon: {
    display: "flex",
    alignItems: "center",
  },
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.7)' },
    '70%': { boxShadow: '0 0 0 10px rgba(255, 152, 0, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)' },
  },
  proceedToast: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    background: '#ecf8f1',
    border: '1px solid #6ac891',
    borderRadius: 10,
    padding: '10px 16px',
    margin: theme.spacing(2, 0),
  },
  proceedToastIcon: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: '#48bb78',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: 13, color: '#fff' },
  },
  proceedToastTitle: { fontSize: '0.8rem', fontWeight: 500, color: '#37955e' },
  proceedToastSub: { fontSize: '0.75rem', color: '#94a3b8' },
}));


const clarityAPIMap = {
  system_model: "llmSystemModelClarity",
  view: "llmViewsClarity",
  transaction: "llmTransactionClarity",
  simulation: "llmSimulationClarity"
}

const workflowSteps = {
  system_model: "System Model",
  database_profile: "Database Model",
  view: "Views",
  transaction: "Transactions",
  simulation: "Simulations"
}

const ChatArea = ({ activeStep, setActiveStep, aiGeneratedItems, setAiGeneratedItems, isAwaitingReview, summaryExist, waitingForAnswer, setWatingForAnswer, autoGenerate, setAutoGenerate, clusterletsList, setClusterletsList }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const router = useHistory();

  const assistantContext = useSelector(getAiAssistantContext)
  const autoProceed = useSelector(getAiAssistantAutoProceed);

  const containerRef = useRef(null);
  const slowTimerRef = useRef(null);
  const selectedDelegateRef = useRef(null);
  const autoProceedRef = useRef(autoProceed);
  const progressIntervalRef = useRef(null);
  const originalAiGeneratedItemsRef = useRef([]);
  const clarityResolverRef = useRef(null);
  const abortControllerRef = useRef(null);
  const stopRequestedRef = useRef(false);
  const oldestLoadedRef = useRef(null);

  const { slug, blueprintId, screen, entityId } = getDetailsFromUrl();
  const PAGE_SIZE = 10;


  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isGenerating, setGenerating] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [entityList, setEntityList] = useState([])
  const [listTitle, setListTitle] = useState('')
  const [sequenceId, setSequenceId] = useState("");
  const [viewList, setViewList] = useState([]);
  const [eventsList, setEventsList] = useState([]);
  const [messagesList, setMessagesList] = useState([]);
  const [transactionList, setTransactionList] = useState([]);
  const [testSuitesList, setTestSuitesLists] = useState([]);
  const [databaseProfilesList, setDatabaseProfilesList] = useState([]);
  const [notificationMessages, setNoficationMessage] = useState([]);
  const [emptyListResponse, setEmptyListResponse] = useState(null);
  const [slowWarning, setSlowWarning] = useState("");
  const [proceedMessage, setProceedMessage] = useState(null);
  const [isEntityGenerating, setIsEntityGenerating] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [prevContext, setPrevContext] = useState([]);
  const [question, setQuestion] = useState(null)
  const [processingEntityId, setProcessingEntityId] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [dbActionType, setDbActionType] = useState('schema');


  const isFullAuto = () => autoProceedRef.current === 'full';
  const isReviewMode = () => autoProceedRef.current === 'review';
  const isAutoRunActive = () => !!autoProceedRef.current && autoProceedRef.current !== 'off';

  useEffect(() => {
    setChatMessages([])
    refreshTrainingData(activeStep);
  }, [activeStep]);



  useEffect(() => {
    if (!proceedMessage) {
      setCountdown(5);
      return;
    }
    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 2) {
          clearInterval(interval);
          return 1;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [proceedMessage]);

  useEffect(() => {
    if (isEntityGenerating) {
      setSlowWarning("");
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
      slowTimerRef.current = setTimeout(() => {
        setSlowWarning("Generation is taking longer than expected - still processing");
        scrollToNewChat();
      }, 75000);
    } else {
      setSlowWarning("");
      if (slowTimerRef.current) {
        clearTimeout(slowTimerRef.current);
      }
    }

    return () => {
      if (slowTimerRef.current) {
        clearTimeout(slowTimerRef.current);
      }
    };
  }, [isEntityGenerating]);

  useEffect(() => {
    if (autoGenerate) {
      setAutoGenerate(false);
      handlePromptSend({ prompt: "Generate from context" })
    }
  }, [autoGenerate])

  useEffect(() => {
    autoProceedRef.current = autoProceed;
    if ((!autoProceed || autoProceed === 'off') && proceedMessage) {
      setProceedMessage(null);
    }
  }, [autoProceed]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
  
    const handleScroll = () => {
      if (container.scrollTop < 100) {
        loadOlderMessages();
      }
    };
  
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activeStep, hasMoreMessages, isLoadingMore]);

  const refreshTrainingData = async (currentStep) => {
    try {
      setLoading(true);
      setChatMessages([]);
      setEmptyListResponse(false);
      setHasMoreMessages(true);
      oldestLoadedRef.current = null;
  
      const response = await dispatch(
        invokeGetService('getLLMTrainingLogs', {
          type: currentStep,
          limit: PAGE_SIZE
        })
      );
      if (response?.status) {
        const chatHistory = buildChatHistory({ step: currentStep, logs: response?.data });
        setChatMessages(attachMessageHandlers(chatHistory));
        setHasMoreMessages(!!response?.hasMore);
        if (response?.data?.length) {
          oldestLoadedRef.current = response.data[0]?.createdAt;
        }
      }
    } catch (error) {
      console.error('Error fetching training logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOlderMessages = async () => {
   
    if (isLoadingMore || !hasMoreMessages || !oldestLoadedRef.current) return;
  
    try {
      setIsLoadingMore(true);
      const container = containerRef.current;
      const prevScrollHeight = container?.scrollHeight || 0;
  
      const response = await dispatch(
        invokeGetService('getLLMTrainingLogs', {
          type: activeStep,
          before: oldestLoadedRef.current,
          limit: PAGE_SIZE
        })
      );
  
      if (response?.status) {
        const olderHistory = buildChatHistory({ step: activeStep, logs: response?.data });
        setChatMessages(prev => [...attachMessageHandlers(olderHistory), ...prev]);
        setHasMoreMessages(!!response?.hasMore);
        if (response?.data?.length) {
          oldestLoadedRef.current = response.data[0]?.id || response.data[0]?.createdAt;
        }
  
        // Preserve scroll position so the view doesn't jump
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - prevScrollHeight;
          }
        });
      }
    } catch (error) {
      console.error('Error loading older messages:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };


  const attachMessageHandlers = (messages) =>
    messages.map(message => {
      if (message.download) {
        return {
          ...message,
          handleDownload: () => handleDownloadSampleData({
            id: message.id
          }),
        };
      }
      return message;
    });

  const handleDownloadSampleData = ({ id }) => {
    try {
      updateMessageById(id, { downloadProgress: 20 });

      dispatch(invokeFileDownloadService('downloadSampleDataFromLog', { traingDataLogId: id }))
        .then(async response => {
          if (!response.ok) {
            const responseJson = await response.json();
            dispatch(handleSnackBar({ open: true, message: responseJson.message, severity: "error" }));
            throw new Error('Network response was not ok');
          }
          updateMessageById(id, { downloadProgress: 50 });
          return response.blob()
        })
        .then(blob => {
          // Create a URL for the downloaded file
          const url = URL.createObjectURL(blob);
          updateMessageById(id, { downloadProgress: 80 });
          // Trigger a download prompt
          const link = document.createElement('a');
          link.href = url;
          link.download = `database_sample_data.json`;
          document.body.appendChild(link);
          link.click();
          URL.revokeObjectURL(url);
          document.body.removeChild(link);

        })
        .catch(error => {
          console.error('Failed to download sample data', error);
        }).finally(() => {
          updateMessageById(id, { downloadProgress: null });
        });
    } catch (error) {
      console.error('Failed to download sample data', error);
      updateMessageById(id, { downloadProgress: null });

    }
  }


  const handleStopGenerationLogUpdate = ({sequence_id, type}) => {
    dispatch(invokePostService('updateLLMLogAsStopped', { sequence_id, type }, [])).then(res => {

    });
  }

  const updateMessageById = (id, changes) => {
    setChatMessages(prev =>
      prev.map(msg => (msg.id === id ? { ...msg, ...changes } : msg))
    );
  };


  //============================== SYSTEM MODEL:START ==========================================
  const generateSystemModel = async (prompt, sequence_id, signal) => {
    let cancelInterval = handleProgressMessages(2000, [
      'Analysing and understanding the use case',
      'Finding all required components',
      'Deciding the role of each component',
      'Identifying sensors, and properties'
    ])


    dispatch(setLlmQuery(prompt))
    try {
      const response = await dispatch(invokePostService('llmGenerationSystemModelNode', { prompt: prompt, sequence_id }, [], false, signal))
      if (response?.aborted) return response;
      cancelInterval()
      if (response?.status) {
        return { status: true, data: response.data }
      } else {
        dispatch(invokePostService('logTimeout', { content: `\n============================== Timeout Error ========================= \nBlueprint: ${blueprintId} \nType: System Model Node \nDate: ${Date.now()} \nxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx` }))
        handleGenerationFailure(response);
        setGenerating(false);
      }
      return response;
    } catch (err) {
      console.error("Error submitting query", err);
      cancelInterval()
      setGenerating(false);
      return { status: false }
    }
  }

  const generateSystemModelConnections = async ({ prompt, entities, sequence_id, signal }) => {
    let cancelInterval = handleProgressMessages(2000, [
      'Defining how components are connected',
      'Building the connection between the nodes',
      'Checking rules and correctness',
      'Finalizing and optimizing the model'
    ])

    try {
      return dispatch(invokePostService('llmGenerationSystemModelConnection', { prompt: prompt, entities, sequence_id }, [], false, signal)).then((response) => {
        cancelInterval()
        // if (response?.status) {
        //   return {status: true, data: response.data, isCreate: response.isCreate}
        // } else {
        //   dispatch(invokePostService('logTimeout', { content: `\n============================== Timeout Error ========================= \nBlueprint: ${blueprintId} \nType: System Model Connection \nDate: ${Date.now()} \nxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx` }))
        //   if (response) {
        //     dispatch(handleSnackBar({ open: true, message: response.message, severity: "error" }));
        //   }
        // }
        return response
      }).catch((error) => {
        cancelInterval()
        console.error('Generating connections from LLM failed. ', error)
      });
    } catch (err) {
      console.error("Generating connections from LLM failed", err);
      cancelInterval()
    }
  }

  const handleProceedSystemModelDeletion = (confirmationReserveData) => {
    pushNewMessage({
      type: activeStep,
      role: "user",
      content: 'Proceed',
    })
    setGenerating(true);
    setIsEntityGenerating(true);
    let cancelInterval = handleProgressMessages(2000, [
      'Applying requested changes',
      'Rebuilding system model connections',
      'Finalizing the updated model'
    ]);
    dispatch(invokePostService('confirmSystemModelDeletion', confirmationReserveData, []))
      .then((response) => {
        cancelInterval();
        if (response?.status) {
          if (screen !== "system-model") {
            router.push(`/blueprint/${slug}/${blueprintId}/system-model`)
          }
          dispatch(setReRender())
          refreshTrainingData("system_model");
        } else {
          handleGenerationFailure(response);
        }
        setGenerating(false);
        setIsEntityGenerating(false);
      })
      .catch((error) => {
        console.error(error)
        setGenerating(false);
        setIsEntityGenerating(false);
        cancelInterval();
      });
  }

  const handleDenyMessage = (message) => {
    pushNewMessage({
      type: activeStep,
      role: "user",
      content: message || 'Deny'
    })
  }
  //============================== SYSTEM MODEL:END ==========================================

  //=============================== TESTSUITE:START ==========================================
  const handleTestuiteUpdate = async (prompt, entity, signal) => {
    try {

      const sequence_id = generateMongoId();
      setSequenceId(sequence_id)
      const summary = await handleClarityChecker({ type: 'simulation', prompt, sequence_id, entity_id: entity.id, signal })

      setGenerating(true);
      if (summary) {
        prompt = summary.detailed_summary
      }

      setGenerating(true)
      let cancelInterval = handleProgressMessages(2000, [
        'Understanding the testsuite',
        'Identifying the testcases',
        'Updating the testcase based on requirement',
        'Reviewing the updated testcase'
      ]);


      const response = await dispatch(invokePostService("llGenerateSimulationTestsuiteUpdate", { prompt, selectedTestsuite: entity.id, sequence_id }, {}, false, signal));
      if (response?.aborted) return response;
      cancelInterval();

      if (response?.status) {
        dispatch(setReRender());
        refreshTrainingData("simulation");
      } else {
        if (response?.testcaseDeletions) {
          pushNewMessage({
            type: activeStep,
            role: 'ai',
            content: response.message,
            proceed: "Proceed",
            revert: "Deny",
            handleProceed: () => handleProceedSimulationDeletion(response.data),
            handleRevert: handleDenyMessage,
          })
          setGenerating(false);
          setIsEntityGenerating(false);
          return
        }

        handleGenerationFailure(response);
      }
      setIsEntityGenerating(false);
      setGenerating(false);

    } catch (error) {
      setGenerating(false)
      setIsEntityGenerating(false);
      console.error('Handling testsuite updated failed.', error);
    }
  }

  const handleProceedSimulationDeletion = (confirmationReserveData) => {
    pushNewMessage({
      type: activeStep,
      role: "user",
      content: 'Proceed'
    })
    setGenerating(true)
    setIsEntityGenerating(true);
    let cancelInterval = handleProgressMessages(2000, [
      'Applying testcase modifications',
      'Removing conflicting testcases',
      'Rebuilding the testsuite structure',
      'Finalizing the testsuite'
    ]);
    try {
      dispatch(invokePostService('llGenerateSimulationConfirmTestsuiteModification', confirmationReserveData, []))
        .then((response) => {
          cancelInterval();
          if (response?.status) {
            dispatch(setReRender());
          } else {
            pushNewMessage({
              type: activeStep,
              role: "ai",
              content: response.message
            })
          }
          refreshTrainingData("simulation");
          setGenerating(false);
          setIsEntityGenerating(false);
        })
        .catch((error) => {
          console.error(`Confirming testsuite updated failed`, error)
          pushNewMessage({
            type: activeStep,
            role: "ai",
            content: "Something went wrong!"
          })
          setGenerating(false);
          setIsEntityGenerating(false);
          cancelInterval();
        });
    } catch (err) {
      setGenerating(false);
      setIsEntityGenerating(false);
      console.error("Confirming testsuite updated failed", err);
    }
  }
  //=============================== TESTSUITE:END ==========================================

  const handleClarityChecker = async (params) => {
    return new Promise(async (resolve, reject) => {

      clarityResolverRef.current = resolve;

      try {
        await processClarityStep(params);
      } catch (err) {
        setWatingForAnswer(false)
        reject(err);
      }

    });
  };

  const processClarityStep = async ({
    type,
    sequence_id,
    prompt,
    answer = null,
    entity_id,
    signal
  }) => {
    try {
      setLoading(true);
      setGenerating(true);
      setWatingForAnswer(false)
      setIsEntityGenerating(false)
      if (entity_id) {
        setProcessingEntityId(entity_id)
      }
      let cancelInterval = handleProgressMessages(3000, [
        'Understanding the requirement',
        'Analyzing requirement clarity',
        'Identifying ambiguities and gaps',
        'Preparing recommendations'
      ])
      const typeMap = {
        system_model: "llmSystemModelClarity",
        view: "llmViewsClarity",
        transaction: "llmTransactionClarity",
        simulation: "llmSimulationClarity",
        database_profile: "databaseClarity"
      };

      const logTypeMap = {
        system_model: "system_model_clarity_check",
        view: "views_clarity_check",
        transaction: "transaction_clarity_check",
        simulation: "simulation_clarity_check"
      };

      let context = [...prevContext]
      // Add latest answer if exists
      if (answer) {
        context.push({
          question: question?.text,
          options: question?.options,
          answer
        })
        setPrevContext(context);
        pushNewMessage({
          content: question?.text,
          role: 'ai',
          type: "answer"
        })
        pushNewMessage({
          content: answer,
          type: "answer"
        })
      }

      setQuestion(null)
      
      const response = await dispatch(
        invokePostService(
          typeMap[type],
          {
            sequence_id,
            prompt,
            prevContext: context,
            entity_id: entity_id || processingEntityId,
            runMode: isAutoRunActive() ? 'auto' : 'manual'
          },
          {},
          false,
          signal
        )
      );
      if (response?.aborted) {
        handleStopGenerationLogUpdate({sequence_id, type: logTypeMap[type]});
        return response;
      }
      setLoading(false);
      setGenerating(false);
      setWatingForAnswer(true);

      if (!response.status) {
        handleGenerationFailure(response);
      }
      console.log({response})
      const clarity = response?.clarityResponse;
      cancelInterval()
      if (!clarity) {
        setWatingForAnswer(false)
        return
      };

      // Final summary
      if (clarity?.state === "ready") {
        setWatingForAnswer(false)
        setProcessingEntityId(null)
        setIsEntityGenerating(true)
        let summary = clarity.summary;

        pushNewMessage({
          role: "ai",
          type: "summary",
          children: <GenerationSummary
            sections={[
              {
                type: 'prompt',
                label: "From your input",
                rows: summary?.from_prompt?.map(d => ({ key: d.label, value: d.value }))
              },
              {
                type: 'answers',
                label: "From your answers",
                rows: summary?.from_answers?.map(d => ({ key: d.label, value: d.value }))
              },
              {
                type: 'assumed',
                label: "Assumed by AI",
                rows: summary?.assumed_by_ai?.map(d => ({ key: d.label, value: d.value }))
              }
            ]}
            isGenerating={true}
            onStopGeneration={handleStopGeneration}
          />
        })
        setPrevContext([])
        clarityResolverRef.current?.(summary);

        return;
      }

      // Next clarification question
      if (clarity?.state === "clarifying") {

        setQuestion({
          ...clarity?.question,
          type: type,
          prompt,
          sequence_id
        })

      }
      setLoading(false)

    } catch (error) {
      console.error(error);
      setWatingForAnswer(false)
    } finally {
      setProcessingEntityId(null)
      setLoading(false);
    }
  };


  const handlePromptSend = async ({ prompt, entity, step, dbActionType }) => {
    const inProgressStep = step || activeStep;
    const signal = beginGenerationSession();

    pushNewMessage({
      "type": inProgressStep,
      "content": prompt,
      "stepTitle": entity ? `@${entity.name}` : null
    })

    let savedPrompt = prompt;
    const selectedEntityId = entity ? String(entity.id) : null;

    setGenerating(true);
    setIsEntityGenerating(true);
    switch (inProgressStep) {
      case 'system_model':
        try {
          dispatch(updateAiAssistant({ requestContext: { entityType: inProgressStep } }));
          const sequence_id = generateMongoId();
          setSequenceId(sequence_id)

          const summary = await handleClarityChecker({ type: inProgressStep, prompt: savedPrompt, sequence_id, signal })
          if (stopRequestedRef.current) return; 
          setGenerating(true);
          if (summary) {
            savedPrompt = summary.detailed_summary
          }
          const data = await generateSystemModel(savedPrompt, sequence_id, signal);
          if (data?.aborted || stopRequestedRef.current) return; 
          if (data.status) {
            const connectionsResponse = await generateSystemModelConnections({ prompt: savedPrompt, entities: data?.data, sequence_id, signal })
            if (connectionsResponse?.aborted || stopRequestedRef.current) return; 
            if (connectionsResponse?.status) {
              if (screen !== "system-model") {
                router.push(`/blueprint/${slug}/${blueprintId}/system-model`)
              }
              dispatch(setReRender())
              await refreshTrainingData("system_model");
              handleAutoProceed({ currentStep: inProgressStep });
            } else if (!connectionsResponse?.status && connectionsResponse?.data) {
              const dependencies = connectionsResponse?.data?.dependencies;

              pushNewMessage({
                type: inProgressStep,
                children: <SystemModelUpdateAlertContent data={dependencies} />,
                role: 'ai',
                proceed: "Proceed",
                revert: "Deny",
                handleProceed: () => handleProceedSystemModelDeletion(connectionsResponse.data),
                handleRevert: handleDenyMessage,

              })
              setGenerating(false);
            } else {
              dispatch(invokePostService('logTimeout', { content: `\n============================== Timeout Error ========================= \nBlueprint: ${blueprintId} \nType: System Model Database profile \nDate: ${Date.now()} \nxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx` }))

              if (connectionsResponse) {
                pushNewMessage({
                  type: inProgressStep,
                  content: connectionsResponse.message,
                  role: 'ai'
                })
                // dispatch(handleSnackBar({ open: true, message: connectionsResponse.message, severity: "error" }));
              }
              setGenerating(false);
            }
            setIsEntityGenerating(false);
          }
        } catch (error) {
          console.error(error)
          setGenerating(false)
          setIsEntityGenerating(false);
        }
        break;
      case 'simulation':
        if (entity) {
          dispatch(updateAiAssistant({ requestContext: { isCreate: false, entity: entity, entityType: inProgressStep } }));
          handleTestuiteUpdate(prompt, entity, signal);
          return;
        }
        dispatch(updateAiAssistant({ requestContext: { isCreate: true, entity: entity, entityType: inProgressStep } }));
        let cancelInterval = handleProgressMessages(2000, [
          'Understanding the transactions',
          'Identifying testable scenarios',
          'Reviewing completeness and gaps',
          'Finalizing testsuite list'
        ])
        const sequence_id = generateMongoId();
        setSequenceId(sequence_id);

        const summary = await handleClarityChecker({ type: inProgressStep, prompt: savedPrompt, sequence_id, signal })
        if (stopRequestedRef.current) return; 
        setGenerating(true);
        if (summary) {
          savedPrompt = summary.detailed_summary
        }

        const generatedScneriosResponse = await dispatch(invokePostService("llGenerateSimulationScenarios", { prompt, sequence_id }, {}, false, signal));
        if (generatedScneriosResponse?.aborted || stopRequestedRef.current) return; 
        cancelInterval()
        const isAutoProceed = shouldAutoProceed(inProgressStep);
        if (!isAutoProceed) {
          setGenerating(false);
        }
        setIsEntityGenerating(false);
        if (generatedScneriosResponse?.status) {
          updateAIGeneratedItemsStatus(generatedScneriosResponse.data || "");
          handleAutoProceed({ currentStep: inProgressStep, isListProceed: true, entitiesToGenerate: generatedScneriosResponse.data });
        } else {
          if (generatedScneriosResponse.transactionsExist === false) {
            dispatch(handleSnackBar({ open: true, message: "No transactions available to find scenarios", severity: "error" }));
          }
          handleGenerationFailure(generatedScneriosResponse);
          setGenerating(false);
        }
        break;
      case 'database_profile':
        const db_sequence_id = generateMongoId();
        let selectedDelegateId = entity ? entity.id : null;
        if (!selectedDelegateId) {
          const response = await dispatch(invokeGetService('fetchDatabaseProfileAndDelegateDetails', {}));
          if (!response?.status) {
            pushNewMessage({
              type: inProgressStep,
              content: 'Something went wrong',
              role: 'ai'
            });
            return;
          }
          
          selectedDelegateId = response?.data?.delegate?._id;
          updateSelectedDelegate({ ...(response?.data?.delegate || {}), id: selectedDelegateId });
        } else {
          updateSelectedDelegate(entity);
        }

        dispatch(updateAiAssistant({ requestContext: { entity: entity, entityType: inProgressStep } }));
        if (dbActionType === "data") {
          generateDatabaseSampleDataWithAI({ prompt: savedPrompt, delegate_id: selectedDelegateId, sequence_id: db_sequence_id, signal });
        } else {
          generateDatabaseTablesWithAI(savedPrompt, selectedDelegateId, inProgressStep, db_sequence_id, signal)
        }
        break
      case 'transaction':
        if (selectedEntityId) { // For handling edit of transaction
          const sequence_id = generateMongoId();
          setSequenceId(sequence_id);
          const summary = await handleClarityChecker({ type: inProgressStep, prompt: savedPrompt, sequence_id, entity_id: selectedEntityId })
          if (stopRequestedRef.current) return; 
          setGenerating(true);
          if (summary) {
            savedPrompt = summary.detailed_summary
          }
          dispatch(updateAiAssistant({ requestContext: { isCreate: false, entity: entity, entityType: inProgressStep } }));
          let cancelInterval = handleProgressMessages(2000, [
            'Analyzing your requirements',
            'Understanding purpose of the transaction',
            'Updating the events',
            'Preparing event type',
            'Applying platform rules and checks',
            'Finalizing the transaction'
          ])
          localStorage.setItem("SHOW_LOADER", true);

          let cancelNotificationInterval;
          const updateNotificationMessages = (items) => {
            cancelNotificationInterval = handleProgressMessages(2000, items || [])
          }
          const response = await generateTransaction(prompt, selectedEntityId, sequence_id, entity.name, updateNotificationMessages, signal);
          if (response?.aborted || stopRequestedRef.current) return; 
          if (cancelNotificationInterval && typeof cancelNotificationInterval === "function") cancelNotificationInterval();
          if (response?.status) {
            refreshTrainingData("transaction");
          } else {
            handleGenerationFailure(response);
          }
          cancelInterval();
          setGenerating(false);
          setIsEntityGenerating(false);
          dispatch(setReRender())
        } else {
          dispatch(updateAiAssistant({ requestContext: { isCreate: true, entity: entity, entityType: inProgressStep } }));
          let cancelInterval = handleProgressMessages(2000, [
            'Understanding the system model and views',
            'Identifying required transactions and coverage',
            'Finalizing transaction list'
          ])
          const sequence_id = generateMongoId();
          setSequenceId(sequence_id);
          const summary = await handleClarityChecker({ type: inProgressStep, prompt: savedPrompt, sequence_id, signal })
          if (stopRequestedRef.current) return; 
          setGenerating(true);
          if (summary) {
            savedPrompt = summary.detailed_summary
          }
          console.log({ savedPrompt, summary })
          const response = await dispatch(invokePostService("llmGenerateTransactionList", { prompt: savedPrompt, sequence_id }, {}, false, signal));
          if (response?.aborted || stopRequestedRef.current) return; 
          cancelInterval()
          if (response?.status) {
            updateAIGeneratedItemsStatus(response.data || []);
            handleAutoProceed({ currentStep: inProgressStep, isListProceed: true, entitiesToGenerate: response.data });
          } else {
            handleGenerationFailure(response);
            setGenerating(false);
          }
          const isAutoProceed = shouldAutoProceed(inProgressStep);
          if (!isAutoProceed) {
            setGenerating(false);
          }
          setIsEntityGenerating(false);
        }
        break;
      case "view":
        if (selectedEntityId) { // To handle edit views
          dispatch(updateAiAssistant({ requestContext: { isCreate: false, entity: entity, entityType: inProgressStep } }));
          const availableViews = [
            ...viewList.map(({ name, desc: description, _id }) => ({ name, description, _id })),
          ];
          let cancelInterval = handleProgressMessages(2000, [
            'Analyzing dashboard requirements',
            'Selecting the right widgets',
            'Organizing sections and components',
            'Validating view and transactions'
          ])
          const sequence_id = generateMongoId();
          setSequenceId(sequence_id);
          const viewToUpdate = viewList.find(tx => tx._id.toString() == selectedEntityId)

          const summary = await handleClarityChecker({ type: inProgressStep, prompt: savedPrompt, sequence_id, entity_id: selectedEntityId, signal })
          if (stopRequestedRef.current) return; 
          setGenerating(true);
          if (summary) {
            savedPrompt = summary.detailed_summary
          }

          const response = await generateSingleView({ prompt: savedPrompt, selectedView: selectedEntityId, availableViews, sequence_id, viewName: viewToUpdate.name, signal });
          if (response?.aborted || stopRequestedRef.current) return; 
          if (!response?.status) {
            handleGenerationFailure(response);
            return
          }
          cancelInterval();

          const viewChanges = response?.data?.views;
          const generatedView = viewChanges?.update?.after?.[0];

          const viewName = generatedView?.name || "";
          const view_type = (viewName.includes("list") || viewName.includes("overview")) ? "summary" : "details";

          let cancelNotificationInterval;
          const updateNotificationMessages = (items) => {
            cancelNotificationInterval = handleProgressMessages(2000, items || [])
          }

          const viewTransactionResult = await generateViewTransactionWithAI({ newView: generatedView, viewChanges, sequence_id, view_type: view_type, setNoficationMessage: updateNotificationMessages, dispatch, signal });
          if (viewTransactionResult?.aborted || stopRequestedRef.current) return; 
          if (viewTransactionResult.status) {
            if (screen === "view" && entityId === selectedEntityId && !generatedView) {
              router.push(`/blueprint/${slug}/${blueprintId}/system-model`);
            } else if (screen === "view" && entityId === selectedEntityId && generatedView?._id && generatedView._id !== entityId && viewTransactionResult.status) {
              router.push(`/blueprint/${slug}/${blueprintId}/view/${generatedView._id}`)
            }
          }
          refreshTrainingData("view");
          if (cancelNotificationInterval && typeof cancelNotificationInterval === "function") cancelNotificationInterval();
          setGenerating(false);
          setIsEntityGenerating(false);
          dispatch(setReRender())
        } else {
          dispatch(updateAiAssistant({ requestContext: { isCreate: false, entityType: inProgressStep } }));
          let cancelInterval = handleProgressMessages(2000, [
            'Analysing the system model',
            'Identifying the suitable views'
          ])
          const sequence_id = generateMongoId();
          setSequenceId(sequence_id);
          const summary = await handleClarityChecker({ type: inProgressStep, prompt: savedPrompt, sequence_id, signal })
          if (stopRequestedRef.current) return; 
          setGenerating(true);
          if (summary) {
            savedPrompt = summary.detailed_summary
          }
          const response = await dispatch(invokePostService("llmGenerateViewsList", { prompt, sequence_id }, {}, false, signal));
          if (response?.aborted || stopRequestedRef.current) return; 
          cancelInterval()
          setIsEntityGenerating(false);
          if (response?.status) {
            updateAIGeneratedItemsStatus(response.data || []);
            handleAutoProceed({ currentStep: inProgressStep, isListProceed: true, entitiesToGenerate: response.data });
          } else {
            handleGenerationFailure(response);
            setGenerating(false);
          }
          const isAutoProceed = shouldAutoProceed(inProgressStep);
          if (!isAutoProceed) {
            setGenerating(false);
          }
        }
        break;
      default:
        break;
    }
    scrollToNewChat();
  }

  const scrollToNewChat = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }

  const pushNewMessage = (data) => {
    setChatMessages(messages => [
      ...messages,
      {
        "id": "new_chat",
        "role": "user",
        "status": true,
        "time": new Date().toISOString(),
        ...data
      }
    ])
  }

  const fetchConstructs = (entityType) => {
    dispatch(invokeGetService('getBlueprintConstructs', {})).then((response) => {
      let data = response?.data;
      setViewList(data.views || []);
      setEventsList(data.events || []);
      setMessagesList(data.messages || [])
      setClusterletsList(data.clusterlets || []);
      setTransactionList(data.transactions || []);
      setTestSuitesLists(data.testSuites || []);
      setDatabaseProfilesList(data.databaseprofiles || []);
      switch (entityType) {
        case 'view':
          setEntityList(data.views?.map(view => ({
            id: view._id,
            name: view.name,
            title: view.name,
            provenance: view.provenance
          })))
          setListTitle('Views')
          break;
        case 'transaction':
          setEntityList(data.transactions?.filter(transaction => transaction.type === 7).map(transaction => ({
            id: transaction._id,
            name: transaction.name,
            title: transaction.name,
            provenance: transaction.provenance
          })))
          setListTitle('Transactions')
          break;
        case 'simulation':
          setEntityList(data.testSuites?.map(testsuite => ({
            id: testsuite._id,
            name: testsuite.name,
            title: testsuite.name,
            provenance: testsuite.provenance
          })))
          setListTitle('Testsuites')
          break;
        case 'database_profile':
          setEntityList(data.clusterlets?.filter(item => item.type === 1 && item?.attributes?.type === "mysql_delegate")?.map(delegate => ({
            id: delegate._id,
            name: delegate.name,
            title: delegate.name,
            provenance: delegate.provenance
          })));
          setListTitle('Delegates')
          break;
        default:
          setEntityList([])
          setListTitle('')
          break;
      }
    })
  }

  const handleProgressMessages = (delay = 2000, messages) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    let index = 0;
    if (index === 0) {
      setProgressMessage(messages[index])
    }

    const interval = setInterval(() => {
      index++;
      setProgressMessage(index >= messages.length ? messages[messages.length - 1] : messages[index])
    }, delay);

    progressIntervalRef.current = interval;

    const cancel = () => {
      clearInterval(interval);
      progressIntervalRef.current = null;
      setProgressMessage("")
    };

    if (index === messages.length - 1) {
      cancel()
    }

    return cancel;
  }


  useEffect(() => {

    if (assistantContext?.entityType && !isEntityGenerating) {
      fetchConstructs(assistantContext?.entityType)
    }
  }, [assistantContext, isEntityGenerating])

  useEffect(() => {
    scrollToNewChat()
  }, [chatMessages, question]);


  useEffect(() => {
    dispatch(updateAiAssistant({ loading: isGenerating }));
  }, [isGenerating])

  const generateDatabaseTablesWithAI = (prompt, delegate_id, currentStep, sequence_id, signal) => {
    try {
      let cancelInterval = handleProgressMessages(2000, [
        'Analysing and understanding the clusterlets',
        'Finding all required tables',
        'Deciding the role of each table',
        'Identifying fields for the tables'
      ]);

      setSequenceId(sequence_id);
      dispatch(invokePostService('llmGenerationSystemModelDBTables', { delegate_id, sequence_id, prompt }, {}, false, signal)).then((response) => {
        if (response?.aborted || stopRequestedRef.current) return; 
        if (response?.status) {
          updateAIGeneratedItemsStatus(response.data.databaseStructureTables || []);
          handleAutoProceed({ isListProceed: true, currentStep, entitiesToGenerate: response.data.databaseStructureTables });
        } else {
          handleGenerationFailure(response);
          setGenerating(false);
        }
        const isAutoProceed = shouldAutoProceed(currentStep);
        if (!isAutoProceed) {
          setGenerating(false);
        }
        setIsEntityGenerating(false);
        cancelInterval();

      }).catch((error) => {
        setGenerating(false);
        setIsEntityGenerating(false);
        cancelInterval();
        handleGenerationFailure({ status: false });
        console.error("Generating database profiles failed. ", error);
      });

    } catch (error) {
      setGenerating(false);
      setIsEntityGenerating(false);
      console.error("Generating database profiles failed. ", error);
      dispatch(setAiRequestEntity(null));
      handleGenerationFailure({ status: false });
    }
  }


  const generateDatabaseSampleDataWithAI = ({ prompt, delegate_id, sequence_id, signal }) => {
    try {
      let cancelInterval = handleProgressMessages(2000, [
        'Analysing the database schema',
        'Understanding table relationships',
        'Planning realistic sample data',
        'Generating records for each table',
        'Maintaining foreign key integrity',
        'Validating generated sample data'
      ]);

      setSequenceId(sequence_id);
      dispatch(invokePostService('llmGenerateDatabaseSampleData', { delegate_id, sequence_id, prompt }, {}, false, signal)).then((response) => {
        if (response?.aborted || stopRequestedRef.current) return; 
        if (response?.status) {
          refreshTrainingData("database_profile");
          dispatch(setReRender());

          const isAutoProceed = shouldAutoProceed("database_profile");
          if (!isAutoProceed) {
            setGenerating(false);
          }
          handleAutoProceed({ currentStep: "database_profile" })

        } else {
          handleGenerationFailure(response);
        }
        setIsEntityGenerating(false);
        cancelInterval();
      }).catch((error) => {
        setGenerating(false);
        setIsEntityGenerating(false);
        cancelInterval();
        handleGenerationFailure({ status: false });
        console.error("Generating database sample data failed. ", error);
      });

    } catch (error) {
      setGenerating(false);
      setIsEntityGenerating(false);
      console.error("Generating database sample data failed. ", error);
      dispatch(setAiRequestEntity(null));
      handleGenerationFailure({ status: false });
    }
  }


  const onAbortGeneration = () => {
    const generatedItems = aiGeneratedItems;
    clearAiGeneratedItems();
    setChatMessages(messages => [
      ...messages,
      {
        "id": "new_chat",
        "status": true,
        "time": new Date().toISOString(),
        "type": activeStep,
        "content": generatedItems,
        "role": 'ai'
      }
    ])
    handleDenyMessage('Discard')
  }

  const onProceedGeneration = async ({ entitiesToGenerate, currentStep }) => {
    try {
      const step = currentStep || activeStep;
      setGenerating(true);
      const signal = beginGenerationSession();
      if (step === "transaction") {
        handleProceedTransactionsGeneration(entitiesToGenerate, signal);
      } else if (step === "simulation") {
        handleProceedSimulationGeneration(entitiesToGenerate, signal)
      } else if (step === "view") {
        handleProceedViewsGeneration(entitiesToGenerate, signal);
      } else if (step === "database_profile") {
        proceedDatabaseProfileSchemaGeneration(entitiesToGenerate, signal);
      }
      scrollToNewChat();

    } catch (error) {
      console.error(error);
    }
  };


  const handleProceedSimulationGeneration = async (modifiedSimulationList, signal) => {
    try {
      setGenerating(true);
      const sequence_id = sequenceId || generateMongoId();

      const newList = modifiedSimulationList?.map(item => ({ name: item.name, description: item.description })) || []
      const isSimulationsModified = hasArrayDifference(originalAiGeneratedItemsRef.current, newList);
    
      if (isSimulationsModified) {
        const updateData = { simulation_scenarios: newList }
        await dispatch(invokePostService("updateLLMResponseInTrainingData", { sequence_id, updateData, type: "simulation" }, {}));
      }

      let localItems = [...modifiedSimulationList];
      for (const [index, item] of modifiedSimulationList.entries()) {
        setIsEntityGenerating(true);
        const { blueprintId } = getDetailsFromUrl();
        const id = item.name;
        if (!blueprintId) return;
        try {
          setNoficationMessage([
            'Understanding test objectives',
            'Selecting related transactions and flows',
            'Defining testsuites and testcases',
            'Applying validation rules',
            'Finalizing the test suite'
          ]);
          updateProgressItem(id, {
            status: "generating",
          });

          // const prompt = [item];

          const response = await generateTestsuites({ selected_scenario: item, all_scenarios: modifiedSimulationList, sequence_id, signal });
          if (response?.aborted) return; 

          const newStatus = response?.status ? "completed" : "error";
          updateProgressItem(id, { status: newStatus });
          localItems = localItems.map(i =>
            i.name === id ? { ...i, status: newStatus } : i
          );
          if (response?.status) {
            dispatch(setInnerScreenRender());
          }
          setIsEntityGenerating(false);
        } catch (error) {
          console.error("TestSuite generation failed:", error);
          setIsEntityGenerating(false);
          updateProgressItem(id, {
            status: "error",
          });
        }
      }
      handleStepCompletion({ currentStep: "simulation", entitiesToGenerate: localItems });
    } catch (error) {
      setGenerating(false);
      dispatch(updateAiAssistant({ requestContext: {} }));
      clearAiGeneratedItems();
      console.error("Testsuites generation failed:", error);
    }
  }

  const generateTestsuites = async ({ selected_scenario, all_scenarios, sequence_id, signal }) => {
    return await dispatch(invokePostService("llGenerateSimulationTestsuites", { selected_scenario, all_scenarios, sequence_id }, {}, false, signal));
  };

  const proceedSampleDataGenerationInReviewMode = ({ delegate_id, sequence_id }) => {
    setDbActionType("data");
    pushNewMessage({
      type: activeStep,
      role: "user",
      content: 'Proceed',
    })
    setGenerating(true);
    generateDatabaseSampleDataWithAI({ prompt: "", delegate_id, sequence_id });
  }

  const skipSampleDataGenerationInReviewMode = () => {
    pushNewMessage({
      id: "skip_sample_data_message",
      type: "database_profile",
      role: "user",
      content: 'Skip'
    });
    handleAutoProceed({ currentStep: "database_profile" });
  }

  // To proceed with database structure generation
  const proceedDatabaseProfileSchemaGeneration = async (modifiedItems, signal) => {
    const delegateId = String(selectedDelegateRef.current?.id);
    const sequence_id = sequenceId || generateMongoId();

    const isTablesModified = hasArrayDifference(originalAiGeneratedItemsRef.current, modifiedItems);
    if (isTablesModified) {
      const updateData = { tables: stripStatus(modifiedItems) };
      await dispatch(invokePostService("updateLLMResponseInTrainingData", { sequence_id, updateData, type: "database_profile" }, {}));
    }
    try {
      setGenerating(true);
      setIsEntityGenerating(true);
      setNoficationMessage([
        "Generating table structures",
        "Defining column data types",
        "Assigning primary keys",
        "Establishing foreign key relationships",
        "Applying column constraints",
        "Creating indexes for optimized queries",
        "Validating table relationships and constraints",
        "Optimizing schema for performance",
        "Finalizing database schema"
      ]);

      updateAIGeneratedItemsStatus(modifiedItems, "generating");

      dispatch(invokePostService('llmGenerationSystemModelDBProfileSchema', { delegate_id: delegateId, sequence_id, database_tables: modifiedItems, isCreate: true }, {}, false, signal)).then((response) => {
        if (response?.aborted || stopRequestedRef.current) return; 
        if (response?.status) {
          updateAIGeneratedItemsStatus(modifiedItems, "completed");
          dispatch(setReRender());
          setTimeout(() => {
            clearAiGeneratedItems();
            refreshTrainingData("database_profile");
            dispatch(setAiRequestEntity(null))
            const isAutoProceed = shouldAutoProceed("database_profile");
            if (!isAutoProceed) {
              setGenerating(false);
              if (Object.keys(response?.data?.dbProfile?.sampleData ?? {}).length) {
                setTimeout(() => {
                  pushNewMessage({
                    id: "sample_data_message",
                    type: "database_profile",
                    role: 'ai',
                    content: `The database schema has changed since the sample data was generated. Regenerate the sample data to ensure it matches the latest schema.`,
                    proceed: "Regenerate",
                    revert: "Skip",
                    handleProceed: () => proceedDatabaseSampleDataGeneration({ delegate_id: delegateId, sequence_id }),
                    handleRevert: () => skipDatabaseSampleDataGeneration()
                  });
                }, 2000)
              }
            }
            if (isReviewMode()) {
              setTimeout(() => {
                pushNewMessage({
                  id: "sample_data_message",
                  type: "database_profile",
                  role: 'ai',
                  content: `Your database schema is ready. Would you like me to generate sample data for it?`,
                  proceed: "Proceed",
                  revert: "Skip",
                  handleProceed: () => proceedSampleDataGenerationInReviewMode({ delegate_id: delegateId, sequence_id }),
                  handleRevert: () => skipSampleDataGenerationInReviewMode()
                });
              }, 2000)
            } else if (isFullAuto()) {
              setTimeout(() => {
                setDbActionType("data");
                pushNewMessage({
                  type: activeStep,
                  role: "user",
                  content: '',
                })
                setGenerating(true);
                generateDatabaseSampleDataWithAI({ prompt: "", delegate_id: delegateId, sequence_id });
              }, 2000)
            } else {
              setIsEntityGenerating(false);
            }
          }, 1000);
        } else {
          updateAIGeneratedItemsStatus(modifiedItems, "error");
          setTimeout(() => {
            clearAiGeneratedItems();
            handleGenerationFailure(response);
            dispatch(setAiRequestEntity(null))
            setGenerating(false);
            setIsEntityGenerating(false);
          }, 1000);
        }

      }).catch((error) => {
        console.error("Generating database profiles failed. ", error);
        setGenerating(false);
        setIsEntityGenerating(false);
        clearAiGeneratedItems();
        dispatch(setAiRequestEntity(null))
      });

    } catch (error) {
      console.error("Generating database profiles failed. ", error);
      setGenerating(false);
      setIsEntityGenerating(false);
      clearAiGeneratedItems();
      dispatch(setAiRequestEntity(null))
    }
  }


  const proceedDatabaseSampleDataGeneration = ({ delegate_id, sequence_id }) => {
    pushNewMessage({
      type: activeStep,
      role: "user",
      content: 'Regenerate',
    })
    setGenerating(true);
    generateDatabaseSampleDataWithAI({ prompt: "", delegate_id, sequence_id })
  }

  const skipDatabaseSampleDataGeneration = () => {
    setGenerating(false);
    pushNewMessage({
      id: "skip_sample_data_message",
      type: "database_profile",
      role: "user",
      content: 'Skip'
    })
  }

  const validateNames = ({ modifiedItems = [], entities = [], entityLabel = "", nameKey = "name" }) => {

    const normalize = (value) => value?.trim().toLowerCase();
    const errors = [];

    const seenNames = new Set();
    const existingNames = new Set(
      entities
        .map(e => normalize(e?.[nameKey]))
        .filter(Boolean)
    );

    modifiedItems.forEach((item, index) => {
      const normalizedName = normalize(item[nameKey]);

      if (!normalizedName) return;

      if (seenNames.has(normalizedName)) {
        errors.push({
          type: "duplicate_in_list",
          index: index,
          key: `${item.name}_${index}`,
          message: `A ${entityLabel?.toLowerCase()} with this name already exists in the list.`
        });
        return; // stop checking further for this item
      }

      seenNames.add(normalizedName);

      if (existingNames.has(normalizedName)) {
        errors.push({
          type: "duplicate_in_existing",
          index: index,
          key: `${item.name}_${index}`,
          message: `This ${entityLabel?.toLowerCase()} name is already in use`
        });
      }
    });

    return errors;
  };


  const validateViewWithExisting = (updatedList) => {
    return validateNames({
      modifiedItems: updatedList,
      entities: viewList,
      entityLabel: "View"
    });
  };

  const validateTxWithExisting = (updatedList) => {
    return validateNames({
      modifiedItems: updatedList,
      entities: transactionList,
      entityLabel: "Transaction"
    });
  };

  const validateTestSuitesWithExisting = (updatedList) => {
    return validateNames({
      modifiedItems: updatedList,
      entities: testSuitesList,
      entityLabel: "TestSuites"
    });
  };

  const validateDatabaseProfileWithExisting = (updatedList) => {
    return validateNames({
      modifiedItems: updatedList,
      entities: [],
      entityLabel: "Table"
    });
  };

  const validations = {
    view: validateViewWithExisting,
    transaction: validateTxWithExisting,
    testsuite: validateTestSuitesWithExisting,
    table: validateDatabaseProfileWithExisting
  }


  const generateTransaction = async (prompt, selectedTransaction, sequence_id, transactionName, updateNotificationMessages, signal) => {
    try {
      const transactionDetails = await dispatch(invokePostService("llmGenerateTransactionEvents", { prompt, selectedTransaction, sequence_id, transactionName }, {}, false, signal));
      if (transactionDetails?.aborted || !transactionDetails?.status) return transactionDetails; 

      updateNotificationMessages([
        'Analyzing transaction structure',
        'Inspecting events and message schema',
        'Identifying business logic',
        'Mapping logic to handlers',
        'Generating handler comments',
        'Finalizing documentation'
      ]);
      const handlerStatus = await dispatch(invokePostService("llmGenerateFunctionalTransactionHandlers", {
        generatedTransactions: transactionDetails.generatedTransactions,
        generatedMessages: transactionDetails.generatedMessages,
        selectedTransactionData: transactionDetails.selectedTransactionData,
        sequenceId: sequence_id
      }, {}, false, signal));
      if (handlerStatus?.aborted || !handlerStatus.status) return handlerStatus; 
     

      updateNotificationMessages([
        'Generating handler code structure',
        'Implementing business logic',
        'Mapping inputs and outputs',
        'Validating handler behavior',
        'Optimizing generated code',
        'Finalizing handler code'
      ]);
      const handlerCodeStatus = await dispatch(invokePostService("generateTransactionHandlerCode", {
        undoData: handlerStatus.undoData,
        entityChangeSet: handlerStatus.entityChangeSet,
        isEdit: handlerStatus.isEdit,
        sequenceId: sequence_id
      }, {}, false, signal));
      if (handlerCodeStatus?.aborted || !handlerCodeStatus.status) return handlerCodeStatus; 

      return handlerStatus
    } catch (error) {
      console.error("Transaction generation failed:", error);
      return { status: false, error: error.message || "Unknown error" };
    }
  };

  const handleProceedTransactionsGeneration = async (updatedTransactionsList, signal) => {
    try {
      dispatch(setAiRequestEntity("transaction"))
      const sequence_id = sequenceId || generateMongoId();
      const isTransactionsModified = hasArrayDifference(originalAiGeneratedItemsRef.current, updatedTransactionsList);
      if (isTransactionsModified) {
        const updateData = { transactions: stripStatus(updatedTransactionsList) }
        await dispatch(invokePostService("updateLLMResponseInTrainingData", { sequence_id, updateData, type: "transaction" }, {}));
      }

      let localItems = [...updatedTransactionsList];
      for (const [index, item] of updatedTransactionsList.entries()) {
        setIsEntityGenerating(true);
        const id = item.name;
        const { blueprintId } = getDetailsFromUrl();

        if (!blueprintId) return;
        try {
          setNoficationMessage([
            'Understanding purpose of the transaction',
            'Selecting participating components',
            'Defining the events',
            'Preparing event type',
            'Applying platform rules and checks'
          ]);
          updateProgressItem(id, {
            status: "generating",
          });

          const prompt = `${item.name} - ${item.description}`;
          const response = await generateTransaction(prompt, undefined, sequence_id, item.name, setNoficationMessage, signal);
          if (response?.aborted || stopRequestedRef.current) return; 
          const newStatus = response?.status ? "completed" : "error";
          updateProgressItem(id, { status: newStatus });
          localItems = localItems.map(i =>
            i.name === id ? { ...i, status: newStatus } : i
          );
          dispatch(setInnerScreenRender());
          if (index === 0 && screen !== "transaction" && response?.data && response?.data._id) {
            router.push(`/blueprint/${slug}/${blueprintId}/transaction/${response.data._id}`)
          }
          setIsEntityGenerating(false);
        } catch (error) {
          console.error("Transaction generation failed:", error);
          setIsEntityGenerating(false);
          updateProgressItem(id, {
            status: "error",
          });
        }
      }

      handleStepCompletion({ currentStep: "transaction", entitiesToGenerate: localItems });
    } catch (error) {
      setGenerating(false);
      setIsEntityGenerating(false);
      clearAiGeneratedItems();
      console.error("Transaction generation failed:", error);
    }
  }

  const generateSingleView = async ({ prompt, selectedView, availableViews, type, sequence_id, viewName, signal }) => {
    try {

      const viewResponse = await dispatch(invokePostService("llmGenerateViews", { prompt, selectedView, availableViews, type, sequence_id, viewName }, {}, false, signal));

      return viewResponse;
    } catch (error) {
      console.error("View generation failed. ", error);
      return { status: false, message: "View generation failed." }
    }
  };

  const createDbResponseFailMessage = async () => {
    try {
      const newMesssage = {
        _id: generateMongoId(),
        name: "msg_db_rsp_fail",
        type: 17,
        fromschema: false,
        blueprint_id: blueprintId
      };
      const fieldMessage = {
        name: "message",
        parent: [{ id: String(newMesssage._id), type: "message" }],
        type: 12,
        attributes: {
          desc: "Message",
          type: "string"
        },
        order: 0,
        blueprint_id: blueprintId
      }
      const fieldStatus = {
        name: "status",
        parent: [{ id: String(newMesssage._id), type: "message" }],
        type: 12,
        attributes: {
          desc: "Status",
          type: "string"
        },
        order: 0,
        blueprint_id: blueprintId
      }
      await dispatch(invokePostService('createMessage', { messageData: newMesssage }, []));
      const payloadMessage = { field: fieldMessage, parentId: fieldMessage.parent[0].id, parentType: "message", eventType: true }
      const payloadStatus = { field: fieldStatus, parentId: fieldStatus.parent[0].id, parentType: "message", eventType: true }

      const result = await Promise.all([
        dispatch(invokePostService('createField', payloadMessage)),
        dispatch(invokePostService('createField', payloadStatus))
      ]);
      return result;
    } catch (error) {
      console.error(error)
    }
  }
  const handleProceedViewsGeneration = async (modifiedViewsList, signal) => {
    try {
      setGenerating(true);
      const sequence_id = sequenceId || generateMongoId();

      const isViewsModified = hasArrayDifference(originalAiGeneratedItemsRef.current, modifiedViewsList);

      if (isViewsModified) {
        const updateData = { views: stripStatus(modifiedViewsList) }
        await dispatch(invokePostService("updateLLMResponseInTrainingData", { sequence_id, updateData, type: "view" }, {}));
      }

      const dbResponseFailMessage = messagesList.find(item => item.name === "msg_db_rsp_fail");
      if (!dbResponseFailMessage) {
        await createDbResponseFailMessage();
      }


      const availableViews = [
        ...viewList.map(({ name, desc: description, _id }) => ({ name, description, _id })),
        ...modifiedViewsList.map(v => ({ ...v, _id: generateMongoId() }))
      ];
      let localItems = [...modifiedViewsList];

      for (const [index, item] of modifiedViewsList.entries()) {
        setIsEntityGenerating(true);
        const id = item.name;
        const { blueprintId } = getDetailsFromUrl()
        if (!blueprintId) return;
        try {
          setNoficationMessage([
            'Analyzing dashboard requirements',
            'Selecting the right widgets',
            'Organizing sections and components'
          ]);

          updateProgressItem(id, {
            status: "generating",
          });

          const prompt = `${item.name} - ${item.description}`;

          const response = await generateSingleView({ prompt, availableViews, type: item.view_type, sequence_id, viewName: item.name, signal });
          if (response?.aborted || stopRequestedRef.current) return; 
          const viewChanges = response?.data?.views;
          const generatedView = viewChanges?.create?.[0];

          const viewTransactionResult = await generateViewTransactionWithAI({ newView: generatedView, sequence_id, view_type: item.view_type, viewChanges, setNoficationMessage, dispatch, isCreate: true, signal });

          dispatch(setInnerScreenRender());

          const viewId = generatedView?._id;
          if (viewTransactionResult.status && index === 0 && screen !== "view" && viewId) {
            router.push(`/blueprint/${slug}/${blueprintId}/view/${viewId}`)
          }
          const newStatus = viewTransactionResult?.status ? "completed" : "error";
          updateProgressItem(id, { status: newStatus });
          localItems = localItems.map(i =>
            i.name === id ? { ...i, status: newStatus } : i
          );

          setIsEntityGenerating(false);
        } catch (error) {
          console.error("View generation failed:", error);
          updateProgressItem(id, {
            status: "error",
          });
          setIsEntityGenerating(false);
        }
      }

      handleStepCompletion({ currentStep: "view", entitiesToGenerate: localItems });
    } catch (error) {
      setGenerating(false);
      setIsEntityGenerating(false);
      clearAiGeneratedItems();
      dispatch(setAiRequestEntity(null))
      console.error("View generation failed:", error);
    }
  }

  const handleStepCompletion = ({ currentStep, entitiesToGenerate }) => {
    const anyFailedGeneration = entitiesToGenerate.some(item => item.status === "error");
    const allFailed = entitiesToGenerate.every(item => item.status === "error");

    const failedCount = entitiesToGenerate.filter(item => item.status === "error").length;
    const totalCount = entitiesToGenerate.length;
    // setIsEntityGenerating(false);
    const functionalTransactions = getFunctionalTransactions({
      transactions: transactionList,
      events: eventsList,
      clusterlets: clusterletsList
    });

    if (anyFailedGeneration && autoProceedRef.current) {
      if (allFailed && functionalTransactions.length === 0 && currentStep === 'transaction') {
        setGenerating(false);
        clearAiGeneratedItems();
        dispatch(setAiAutoProceed(false));
        pushNewMessage({
          id: "step_completion_message",
          status: false,
          type: currentStep,
          role: 'ai',
          content: `All ${totalCount} ${workflowSteps[currentStep]} failed to generate. Please review and try again.`,
        });
        return;
      }
      setTimeout(() => {
        pushNewMessage({
          id: "step_completion_message",
          type: currentStep,
          role: 'ai',
          content: `${failedCount} of ${totalCount} ${workflowSteps[currentStep]} failed to generate. Would you like to continue to the next step or stay here to review the failures?`,
          proceed: "Continue to Next Step",
          revert: "Stop & Review",
          handleProceed: () => handleAutoProceedAnyway(currentStep),
          handleRevert: () => handleStopAutoProceed(),
        });
      })
      return;
    }
    clearAiGeneratedItems();
    const isAutoProceed = shouldAutoProceed(activeStep);
    if (!isAutoProceed) {
      setGenerating(false);
    }
    refreshTrainingData(currentStep);
    dispatch(setAiRequestEntity(null));
    handleAutoProceed({ currentStep });
  }

  const handleAutoProceedAnyway = (currentStep) => {
    dispatch(setAiRequestEntity(null));
    handleAutoProceed({ currentStep });

  }

  const handleStopAutoProceed = () => {
    setGenerating(false);
    clearAiGeneratedItems();
    // dispatch(setAiAutoProceed("off"));
    pushNewMessage({
      id: "step_completion_message",
      type: activeStep,
      role: "user",
      content: 'Stop & Review'
    })
  }


  const updateAIGeneratedItemsStatus = (items = [], status = 'pending') => {
    if (!Array.isArray(items)) return;
    if (items.length === 0) setEmptyListResponse(true)

    originalAiGeneratedItemsRef.current = items;
    const updatedItems = items.map(item => ({
      ...item,
      status,
    }));

    setAiGeneratedItems(updatedItems);
    scrollToNewChat();
  };

  const clearAiGeneratedItems = () => {
    originalAiGeneratedItemsRef.current = [];
    setAiGeneratedItems([]);
  };

  const stripStatus = (items) => items.map(({ status, ...rest }) => rest);



  const updateProgressItem = (id, changes) => {
    setAiGeneratedItems((prev) => {
      return prev.map((p) => {
        if (p.name !== id) return p;
        const updated = { ...p, ...changes };
        return updated;
      });
    });
  };


  const handleGenerationFailure = (response) => {
    setGenerating(false);
    if (response && !response.status && response.statusCode === 504) {
      pushNewMessage({
        "type": activeStep,
        "role": "ai",
        "status": false,
        "content": "Generation timed out. Please try again.",
      });
    } else if (response && !response.status) {
      const content = (response.message && response.isLLMError) ? response.message : "Generation failed. Please try again."
      pushNewMessage({
        "type": activeStep,
        "role": "ai",
        "status": false,
        "content": content
      });
    }
  }

  const updateSelectedDelegate = (data) => {
    selectedDelegateRef.current = data;
  };

  const shouldAutoProceed = (currentStep) => {
    const step = currentStep || activeStep;
    if (!isAutoRunActive()) return false;

    const functionalTransactions = getFunctionalTransactions({
      transactions: transactionList,
      events: eventsList,
      clusterlets: clusterletsList
    });

    const counts = {
      databaseProfiles: databaseProfilesList.length,
      views: viewList.length,
      transactions: functionalTransactions.length,
      simulations: testSuitesList.length
    };

    const stepConditions = {
      system_model: ["databaseProfiles", "views", "transactions", "simulations"],
      database_profile: ["views", "transactions", "simulations"],
      view: ["transactions", "simulations"],
      transaction: ["simulations"],
      simulation: []
    };

    const requiredEmpty = stepConditions[step];

    if (!requiredEmpty) return false;
    return requiredEmpty.every(key => counts[key] === 0);
  };

  const handleAutoProceed = ({ isListProceed, currentStep, entitiesToGenerate }) => {
    const isAutoProceed = shouldAutoProceed(currentStep);
    if (!isAutoProceed) {
      setGenerating(false);
      return;
    }

    setGenerating(true);
    if (isListProceed) {
      if (isFullAuto()) {
        return onProceedGeneration({ entitiesToGenerate, currentStep });
      } else { // Review mode: show list, stop generating state so user can interact
        setGenerating(false);
        return;
      }
    }
    const nextStep = switchToNextStep(currentStep);
    if (nextStep) {
      setProceedMessage(`${workflowSteps[currentStep]} is generated`);
    } else if (currentStep === "simulation") {
      setProceedMessage(`Your Blueprint is ready!`);
      setGenerating(false);
    }
    scrollToNewChat();
    setTimeout(() => {
      setProceedMessage(null);
      if (nextStep) {
        if (!isAutoRunActive()) {
          setGenerating(false);
          return;
        }
        setActiveStep(nextStep);
      }
    }, 6000);
    setTimeout(() => {
      if (nextStep) {
        if (!isAutoRunActive()) {
          setGenerating(false);
          return;
        }
        handlePromptSend({ prompt: "Generate from context", step: nextStep });
      }
    }, 7000);
  };

  const switchToNextStep = (currentStep) => {
    const setpFlow = [
      "system_model",
      "database_profile",
      "view",
      "transaction",
      "simulation"
    ];
    const currentIndex = setpFlow.indexOf(currentStep);
    const nextStep = setpFlow[currentIndex + 1];
    return nextStep;
  };



  const beginGenerationSession = () => {
    abortControllerRef.current = new AbortController();
    stopRequestedRef.current = false;
    return abortControllerRef.current.signal;
  };

  const handleStopGeneration = () => {
  
    stopRequestedRef.current = true;
    abortControllerRef.current?.abort();
  
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (slowTimerRef.current) {
      clearTimeout(slowTimerRef.current);
    }
    setAiGeneratedItems([]);
    setProgressMessage("");
    setSlowWarning("");
    setGenerating(false);
    setIsEntityGenerating(false);
    setWatingForAnswer(false);
    setQuestion(null);
    clearAiGeneratedItems();
    dispatch(setAiRequestEntity(null));
  
    pushNewMessage({
      id: "generation_stopped_message",
      type: activeStep,
      role: "ai",
      status: true,
      content: "Generation cancelled. Partial results may be incomplete."
    });
  };
  console.log({question}, 'this is question')
  return (
    <Box style={{ display: 'flex', flexDirection: 'column', flex: '1', minWidth: 0 }}>
      {/* Message loader */}
      {loading && (
        <Box className={classes.loaderContainer}>
          <LinearProgress className={classes.linearProgress} />
          <div className={classes.loaderGlow} />
        </Box>
      )}

      {isGenerating && slowWarning && (
        <Alert
          severity="warning"
          classes={{ icon: classes.alertIcon }}
          icon={
            <div
              className={`${classes.pulseDot} ${isGenerating ? classes.animatedPulseDot : ""
                }`}
            />
          }
        >
          {slowWarning}
        </Alert>
      )}

      <Box className={classes.container} ref={containerRef}>

        {isLoadingMore && (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
            <Typography style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              Loading older session logs…
            </Typography>
          </Box>
        )}

        {/* Message Bubbles and generation loader */}
        {
          (loading || chatMessages.length || (autoProceed && isGenerating)) &&
          <Box style={{ minHeight: '90%' }}>
            {chatMessages.filter(m => m.id !== "step_completion_message").map((message) => (
              <MessageBubble
                key={`${message.time}-${message.role}`}
                noPadding={message.type === "summary"}
                message={message}
              >
                {message.children}
              </MessageBubble>
            ))}

            {
              !loading && aiGeneratedItems.length > 0 &&
              <MessageBubble
                message={{ status: true, role: "ai", time: new Date() }}
              >
                <AIGeneratedList
                  activeStep={activeStep}
                  items={aiGeneratedItems}
                  setItems={setAiGeneratedItems}
                  onAbortGeneration={onAbortGeneration}
                  onProceed={onProceedGeneration}
                  isGenerating={isGenerating}
                  validations={validations}
                  notificationMessages={notificationMessages}
                />
              </MessageBubble>
            }
            {chatMessages
              .filter(m => m.id === "step_completion_message")
              .map((message) => (
                <MessageBubble message={message}>
                  {message.children}
                </MessageBubble>
              ))}
            {
              emptyListResponse &&
              <MessageBubble
                message={{
                  status: true,
                  role: "ai",
                  time: new Date(),
                  content: 'No results generated. Try refining your request.'
                }}
              />
            }

            {
              question &&
              <MessageBubble noPadding message={{ status: true, role: "ai", time: new Date() }}>
                <ClarificationQuestion
                  question={question.text}
                  options={question.options}
                  scopeLabel={question.reason}
                  isMultipleChoiceAllowed={question.is_multiple_choice}
                  onSkip={() => processClarityStep({
                    type: question.type,
                    sequence_id: question?.sequence_id,
                    prompt: question?.prompt,
                    answer: "I want to skip this question.",
                    signal: beginGenerationSession()
                  })}
                  onSubmit={(answer) => processClarityStep({
                    type: question.type,
                    sequence_id: question?.sequence_id,
                    prompt: question?.prompt,
                    answer,
                    signal: beginGenerationSession()
                  })}
                />
              </MessageBubble>
            }

            {isGenerating && progressMessage && progressMessage !== '' &&
              <Typography str style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'flex-end', paddingBottom: '1rem', fontStyle: 'italic' }}>
                {progressMessage}
                <span style={{ paddingLeft: 2 }} class="dot-loader" aria-label="Loading"></span>
              </Typography>
            }
          </Box>
        }

        {/*<MessageBubble noPadding message={{ status: true, role: "ai", time: new Date() }}>
          <GenerationSummary />
        </MessageBubble> */}

        {/* Empty State for the system model */}
        {
          !loading && activeStep === "system_model" && !chatMessages.length &&
          <AIAssistanceEmptyState />
        }

        {
          !loading && activeStep !== "system_model" && !chatMessages.length && !aiGeneratedItems.length &&
          <WorkflowEmptyState
            activeStep={activeStep}
            onAutoGenerate={() => handlePromptSend({ prompt: 'Generate from context' })}
          />
        }

        {proceedMessage && (
          <Box className={classes.proceedToast}>
            <Box className={classes.proceedToastIcon}>
              <CheckCircleIcon style={{ fontSize: 14 }} />
            </Box>
            <Box>
              <Typography className={classes.proceedToastTitle}>{proceedMessage}</Typography>
              {proceedMessage !== "Your Blueprint is ready!" && <Typography className={classes.proceedToastSub}>Continuing workflow in {countdown}s — switch to Manual to stop </Typography>}
            </Box>
          </Box>
        )}


        {/* Input Field */}
        <ChatInput
          handlePromptSend={handlePromptSend}
          entityList={entityList}
          listTitle={listTitle}
          isLoading={isGenerating}
          disableAll={aiGeneratedItems?.length}
          isAwaitingReview={isAwaitingReview}
          isWatingForAnswer={waitingForAnswer}
          summaryExist={summaryExist}
          isSystemModelEdit={clusterletsList?.length > 1}
          onStopGeneration={handleStopGeneration}
          dbActionType={dbActionType}
          setDbActionType={setDbActionType}
        />
      </Box>
    </Box>

  );
};
export default ChatArea;